import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, ArrowRight, ArrowLeft, Sprout } from "lucide-react";

const QUESTIONS = [
  {
    id: "stage",
    q: "Where are you today?",
    options: [
      ["idea", "Just an idea — pre-formulation"],
      ["prototype", "Prototype or sampling only"],
      ["in-market", "In-market with limited retail"],
      ["scaling", "Scaling to national distribution"],
    ],
  },
  {
    id: "revenue",
    q: "Roughly how much revenue are you doing?",
    options: [
      ["0", "Pre-revenue"],
      ["100k", "Under $100K"],
      ["1m", "$100K – $1M"],
      ["5m", "$1M+"],
    ],
  },
  {
    id: "focus",
    q: "What matters most in the next 90 days?",
    options: [
      ["brand", "Building brand and positioning"],
      ["ops", "Operations, sourcing, or co-manufacturing"],
      ["financing", "Fundraising or financing"],
      ["retail", "Landing retail accounts"],
    ],
  },
  {
    id: "timeline",
    q: "How aggressive is your timeline?",
    options: [
      ["3mo", "Ship or shift within 3 months"],
      ["6mo", "Within 6 months"],
      ["12mo", "12 months and beyond"],
    ],
  },
];

const PLAN_LABEL = { free: "Free Access", founder: "Founder Membership", pro: "Founder Pro Membership" };

export default function Assessment() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];

  const choose = async (value) => {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/assessment", next);
      setResult(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="assessment-brand">
            <div className="w-8 h-8 rounded-lg grad-band" />
            <span className="font-display text-xl">CPG Institute</span>
          </Link>
          {!result && (
            <p className="text-sm text-muted-foreground">Question {step + 1} of {total}</p>
          )}
        </div>
        <div className="h-1 bg-muted">
          <div className="h-full grad-band transition-all"
            style={{ width: result ? "100%" : `${((step) / total) * 100}%` }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {!result ? (
          <div>
            <p className="chip mb-4">Founder path assessment</p>
            <h1 className="font-display text-4xl md:text-5xl" data-testid="assessment-question">{current.q}</h1>
            <p className="mt-3 text-muted-foreground">Pick the option closest to your reality — you can change plans later.</p>

            <div className="mt-10 flex flex-col gap-3">
              {current.options.map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => choose(value)}
                  disabled={busy}
                  data-testid={`assessment-option-${value}`}
                  className="group text-left surface p-5 hover:border-primary hover:-translate-y-0.5 transition"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold">{label}</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 text-primary transition" />
                  </div>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button onClick={() => setStep(step - 1)} data-testid="assessment-back"
                className="mt-8 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>
        ) : (
          <div className="text-center" data-testid="assessment-result">
            <div className="inline-flex w-14 h-14 rounded-full grad-band items-center justify-center text-white mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <p className="chip mt-6">Your recommended path</p>
            <h1 className="font-display text-5xl md:text-6xl mt-3">
              Start with <span className="text-grad">{PLAN_LABEL[result.recommended]}</span>
            </h1>
            <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
              Based on your stage, revenue, focus, and timeline. You can always upgrade or downgrade later.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <button className="btn-primary" onClick={() => nav(`/signup?next=/pricing&plan=${result.recommended}`)} data-testid="assessment-signup">
                Create free account <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/account/subscription" className="btn-ghost" data-testid="assessment-see-plans">
                See all plans
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
